import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import Papa from "papaparse";
import api from "../utils/api";

const BulkCategoriesImport = ({
  userId,
  onImportComplete,
  refreshCategories,
}) => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStats, setImportStats] = useState({
    total: 0,
    added: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [showInstructions, setShowInstructions] = useState(true);
  const fileInputRef = useRef(null);

  // Download CSV Template
  const downloadCSVTemplate = () => {
    const headers = ["newTitle", "newStatus", "newDescription"];

    const comments = [
      "# IMPORTANT: Keep the header row exactly as shown",
      "# Required fields: newTitle, newStatus",
      "# newStatus: Must be 'Active' or 'Inactive' (case-sensitive)",
      "# newDescription is optional",
      "# Each category will be created with a sequential srNo",
      "# ==============================================",
    ];

    const sampleData = [
      "Burgers,Active,All types of burger items",
      "Drinks,Active,Cold drinks, shakes, and beverages",
      "Appetizers,Active,Starters and snacks",
      "Main Course,Inactive,Main dishes and meals",
      "Desserts,Active,Sweets and desserts",
      "Pizzas,Active,All pizza varieties",
      "Chinese,Inactive,Chinese cuisine dishes",
      "Italian,Active,Italian food items",
      "Indian,Active,Traditional Indian food",
      "Breakfast,Inactive,Breakfast items and combos",
    ];

    const csvContent = [...comments, headers.join(","), ...sampleData].join(
      "\n"
    );
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk_categories_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("Template downloaded! Fill it with your categories.");
  };

  // Drag and Drop Handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        validateAndSetFile(file);
      } else {
        toast.error("Please upload a CSV file (.csv)");
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    // File size validation (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File too large! Maximum size is 5MB");
      return;
    }

    if (!file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file (.csv extension)");
      return;
    }

    setCsvFile(file);
    resetErrorDisplay();
  };

  const removeFile = () => {
    setCsvFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    resetErrorDisplay();
  };

  const resetErrorDisplay = () => {
    setValidationErrors([]);
    setShowInstructions(true);
    setImportStats({
      total: 0,
      added: 0,
      updated: 0,
      skipped: 0,
      failed: 0,
    });
  };


  // Validate CSV data
  const validateCSVData = (data) => {
    const errors = [];
    const validCategories = [];

    data.forEach((row, index) => {
      const rowNum = index + 2; // +2 because header is row 1 and index starts at 0

      // Check required fields
      if (!row.newTitle || row.newTitle.trim() === "") {
        errors.push(`Row ${rowNum}: Category title is required`);
        return;
      }

      // Validate title length
      if (row.newTitle.trim().length < 2) {
        errors.push(
          `Row ${rowNum}: Category title must be at least 2 characters`
        );
        return;
      }

      if (row.newTitle.trim().length > 100) {
        errors.push(
          `Row ${rowNum}: Category title too long (max 100 characters)`
        );
        return;
      }

      // Check required field: newStatus
      if (!row.newStatus || row.newStatus.trim() === "") {
        errors.push(
          `Row ${rowNum}: Status (newStatus) is required. Use 'Active' or 'Inactive'`
        );
        return;
      }

      const status = row.newStatus.trim();
      if (!["Active", "Inactive"].includes(status)) {
        errors.push(
          `Row ${rowNum}: Status must be exactly 'Active' or 'Inactive' (case-sensitive). Found: '${status}'`
        );
        return;
      }

      // Validate description length if provided
      if (row.newDescription && row.newDescription.length > 500) {
        errors.push(`Row ${rowNum}: Description too long (max 500 characters)`);
        return;
      }

      validCategories.push({
        newTitle: row.newTitle.trim(),
        newStatus: status,
        newDescription: row.newDescription?.trim() || "",
        // srNo: validCategories.length + 1, // Temporary srNo, will be updated
        rowNumber: rowNum,
      });
    });

    return { errors, validCategories };
  };

  // Process CSV Import
  const processCSVImport = async () => {
    if (!csvFile || !userId) {
      toast.error("Please select a CSV file");
      return;
    }

    setIsImporting(true);
    setImportProgress(30);
    setValidationErrors([]);

    try {
      // Parse CSV
      Papa.parse(csvFile, {
        header: true,
        skipEmptyLines: true,
        comments: "#",
        complete: async (results) => {
          try {
            // Validate data
            const { errors, validCategories } = validateCSVData(results.data);

            if (errors.length > 0) {
              setValidationErrors(errors);
              setShowInstructions(false);
              setIsImporting(false);
              return;
            }

            if (validCategories.length === 0) {
              toast.error("No valid categories found in CSV");
              setIsImporting(false);
              return;
            }

            setImportProgress(70);

            const response = await api.post(`bulk-categories/${userId}`, {
              categories: validCategories,
            });

            const stats = response.data.stats || response.data;

            setImportStats({
              total: stats.total || validCategories.length,
              added: stats.added || 0,
              updated: stats.updated || 0,
              failed: stats.failed || 0,
            })

            // Show results
            setImportStats(100);

            if (stats.failed === 0) {
              toast.success(
                `Import complete! Added: ${stats.added}, Updated: ${stats.updated}`
              );
            } else {
              toast.warning(
                `Import partially complete. Added: ${stats.added}, Updated: ${stats.updated}, Failed: ${stats.failed}`
              );
            }

            // Refresh category list
            if (refreshCategories) {
              refreshCategories();
            }

            // Notify parent component
            if (onImportComplete) {
              onImportComplete(stats);
            }

            // Auto-close modal after successful import
            if (stats.failed === 0) {
              setTimeout(() => {
                handleCloseModal();
              }, 2000);
            }
          } catch (error) {
            console.error("Error processing CSV:", error);
            toast.error("Error processing CSV file");
            setValidationErrors([`Processing error: ${error.message}`]);
            setShowInstructions(false);
          } finally {
            setIsImporting(false);
          }
        },
        error: (error) => {
          console.error("CSV parsing error:", error);
          toast.error("Error parsing CSV file");
          setValidationErrors([`CSV parsing error: ${error.message}`]);
          setShowInstructions(false);
          setIsImporting(false);
        },
      });
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Import failed");
      setIsImporting(false);
    }
  };

  const handleCloseModal = () => {
    setShowImportModal(false);
    setCsvFile(null);
    resetErrorDisplay();
    setImportProgress(0);
    setIsImporting(false);
  };

  // Import Modal
  const ImportModal = () => {
    if (!showImportModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="bg-teal-600 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Bulk Categories Import</h2>
                <p className="mt-1 text-teal-100 text-sm">
                  Upload CSV to add/update categories
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-white hover:text-gray-200 text-2xl"
                disabled={isImporting}
              >
                &times;
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {/* Import Stats */}
            {importStats.total > 0 && (
              <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Import Results
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-700">
                      {importStats.total}
                    </div>
                    <div className="text-sm text-gray-600">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {importStats.added}
                    </div>
                    <div className="text-sm text-gray-600">Added</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {importStats.updated}
                    </div>
                    <div className="text-sm text-gray-600">Updated</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {importStats.skipped}
                    </div>
                    <div className="text-sm text-gray-600">Skipped</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {importStats.failed}
                    </div>
                    <div className="text-sm text-gray-600">Failed</div>
                  </div>
                </div>
              </div>
            )}
            {/* Progress Bar */}
            {isImporting && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Importing categories...</span>
                  <span>{importProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-teal-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
            {/* Drag & Drop Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 mb-6 ${
                isDragging
                  ? "border-teal-500 bg-teal-50"
                  : csvFile
                  ? "border-green-500 bg-green-50"
                  : "border-gray-300 hover:border-teal-400 hover:bg-gray-50"
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {csvFile ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <div className="bg-green-100 p-3 rounded-full">
                      <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-800">
                        {csvFile.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {(csvFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={removeFile}
                      className="text-sm text-red-600 hover:text-red-800 font-medium px-4 py-2 border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      Remove File
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-teal-600 hover:text-teal-800 font-medium px-4 py-2 border border-teal-200 rounded-lg hover:bg-teal-50"
                    >
                      Change File
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <svg
                    className="w-16 h-16 mx-auto text-gray-400 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                    ></path>
                  </svg>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Drag & drop your CSV file here
                  </p>
                  <p className="text-gray-500 mb-4">or</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-teal-600 hover:bg-teal-700 text-white font-medium py-2.5 px-8 rounded-lg transition duration-300 shadow-md"
                  >
                    Browse Files
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <p className="text-sm text-gray-500 mt-4">
                    Supports .csv files up to 5MB
                  </p>
                </>
              )}
            </div>
            {/* Error Display Section */}
            {validationErrors.length > 0 ? (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    Validation Errors ({validationErrors.length})
                  </h3>
                  <button
                    onClick={resetErrorDisplay}
                    className="text-sm text-teal-600 hover:text-teal-800 font-medium"
                  >
                    Clear Errors
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-2">
                    {validationErrors.slice(0, 2).map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="mt-1">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                    {validationErrors.length > 2 && (
                      <li className="text-gray-600 italic">
                        ...and {validationErrors.length - 2} more errors
                      </li>
                    )}
                  </ul>
                </div>
                <div className="mt-4 pt-3 border-t border-red-200">
                  <p className="text-sm text-red-600">
                    Please fix these errors and upload again.
                  </p>
                </div>
              </div>
            ) : showInstructions ? (
              /* Instructions Section */
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  Import Instructions
                </h3>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Duplicate handling:</strong> If category with same
                      name exists, it will be updated
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Required field:</strong> newTitle (category name)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Status field:</strong> Must be 'Active' or 'Inactive' (case-sensitive)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Optional field:</strong> newDescription
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Title length:</strong> 2-100 characters
                    </span>
                  </li>

                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Description length:</strong> Max 500 characters
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Sr.No:</strong> Will be assigned automatically
                    </span>
                  </li>
                </ul>
              </div>
            ) : null}

            {/* Download Template Section */}
            <div className="text-center">
              <button
                onClick={downloadCSVTemplate}
                className="inline-flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 font-medium underline-offset-4 hover:underline transition bg-transparent border-none p-0 cursor-pointer focus:outline-none"
                aria-label="Download CSV template for bulk categories import"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
                Download CSV Template
              </button>
              <p className="text-gray-600 text-sm mt-2">
                Fill this template with your categories
              </p>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 p-6 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {csvFile ? `Selected: ${csvFile.name}` : "No file selected"}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-300 font-medium"
                  disabled={isImporting}
                >
                  Cancel
                </button>
                <button
                  onClick={processCSVImport}
                  disabled={!csvFile || isImporting}
                  className={`px-6 py-2.5 rounded-lg transition duration-300 font-medium ${
                    !csvFile || isImporting
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-teal-600 hover:bg-teal-700 text-white shadow-md"
                  }`}
                >
                  {isImporting ? (
                    <span className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Importing...
                    </span>
                  ) : (
                    "Start Import"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Button to open modal */}
      <button
        onClick={() => setShowImportModal(true)}
        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition duration-200 hover:underline"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
          ></path>
        </svg>
        Bulk Import Categories
      </button>

      <ImportModal />
    </>
  );
};

export default BulkCategoriesImport;
